<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Alert,
		Container,
		ControlsContainer,
		EnergyDistribution,
		EnergyDistributionZendure,
		IconButton,
		Modal,
		Toggle,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { getPVSetupDevices } from './pv_setup_helper';
	import { getPvSetupStatuses } from './pv_setup_live_calculate_helper';
	import { getPvDevicePower } from 'src/components/EnergyMonitoring/helpers/pv_flow_helpers';
	import { isTestUser } from 'src/helpers/user_helpers';
	//Components
	import LivePanelInfo from './LivePanelInfo.svelte';
	import EmWidgetState from 'src/components/EnergyMonitoring/EmWidgetState.svelte';
	//Stores
	import {
		createMemberStatusSnapshot,
		createMemberSettingsSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { real_id } from 'src/helpers/interface_helpers';
	//Types
	import type { PVSetupTypes } from './pv_setup_helper';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';

	export let pvSetupType: PVSetupTypes;
	export let staticPreview = false;
	export let embedded = false;

	const getPvLiveStatus = (statuses, settingsSnapshot, pvSetupType: PVSetupTypes) => {
		let gridPower = 0;
		let generatedPower = 0;
		const pvSetupDevices = getPVSetupDevices(pvSetupType);
		pvSetupDevices.gridDevices.forEach((dev) => {
			gridPower +=
				getPvDevicePower(dev, statuses[real_id(dev.id)], settingsSnapshot[real_id(dev.id)]) || 0;
		});
		pvSetupDevices.solarDevices.forEach((dev) => {
			generatedPower +=
				getPvDevicePower(dev, statuses[real_id(dev.id)], settingsSnapshot[real_id(dev.id)]) || 0;
		});
		return getPvSetupStatuses(gridPower, generatedPower, pvSetupType, switchView);
	};

	let showModal = false;
	let switchView = false;

	// let gridPower = -500;
	// let generatedPower = 0;
	// $: pvData = getPvSetupStatuses(gridPower, generatedPower, pvSetupType);

	$: pvSetupDevices = getPVSetupDevices(pvSetupType);
	$: pvDeviceIds = [...pvSetupDevices.gridDevices, ...pvSetupDevices.solarDevices].map(
		(dev) => dev.id,
	);
	$: memberStatusStore = createMemberStatusSnapshot(pvDeviceIds);
	$: memberStatus = $memberStatusStore;
	$: memberSettingsStore = createMemberSettingsSnapshot(pvDeviceIds);
	$: memberSettings = $memberSettingsStore;
	$: pvData =
		staticPreview || $energyMonitoringDataMode === 'demo'
			? getPvSetupStatuses(600, 2400, pvSetupType, switchView)
			: getPvLiveStatus(memberStatus, memberSettings, pvSetupType);
	$: hasActivity =
		staticPreview ||
		$energyMonitoringDataMode === 'demo' ||
		Math.abs(pvData.gridPower) +
			Math.abs(pvData.solarPower) +
			Math.abs(pvData.houseConsumption) +
			Math.abs(pvData.batteryPower) >
			0;
</script>

{#if hasActivity}
	{#key pvSetupType}
		<div class="pv-distribution-container" class:pv-distribution-container--embedded={embedded}>
			<div class="pv-distribution-layout">
				<div class="pv-distribution-chart">
					<ControlsContainer
						class="height-full"
						orientation="vertical"
						color={$showNewLayout ? 'color-1' : undefined}
					>
						<svelte:fragment slot="first">
							<div style="pointer-events:none">
								{#if switchView}
									<EnergyDistributionZendure
										id="energy-distribution-zendure"
										variant="secondary"
										cards={pvData.secondCards}
										paths={pvData.secondPaths}
									/>
								{:else}
									<EnergyDistribution
										id="energy-distribution"
										cards={pvData.cards}
										paths={pvData.paths}
									/>
								{/if}
							</div>
						</svelte:fragment>
						<svelte:fragment slot="topRightSlot">
							{#if isTestUser()}
								<IconButton
									icon="fas fa-cog"
									aria-label={$format('button_pv_setup_switch_live_distributions', {
										default: 'Switch between live energy distribution views',
									})}
									on:click={() => (showModal = true)}
								/>
							{/if}
						</svelte:fragment>
					</ControlsContainer>
				</div>
				<div class="pv-distribution-details">
					<div class="grid">
						<LivePanelInfo {pvSetupType} {pvData} {staticPreview} />
					</div>
				</div>
			</div>
		</div>
	{/key}
{:else}
	<EmWidgetState
		message={$format('energy_monitoring_state_pv_no_activity', {
			default: 'No live PV activity to display.',
		})}
		minHeight="220px"
	/>
{/if}

<Modal
	title="Live energy distributions"
	{showModal}
	removeScrollPadding
	on:close={() => (showModal = false)}
>
	<svelte:fragment slot="content">
		<Container padding="scrollPaddings">
			<Alert
				class="mb-l"
				type="warning"
				heading={'This functionality is under development and used for testing.'}
				message={'You can see this because you are using a test user account.'}
				isHeadingBold
			/>
			<Toggle class="mb-s" size="m" id="switch-between-live-energy" bind:checked={switchView}>
				{$format('button_pv_setup_switch_live_distributions', {
					default: 'Switch between live energy distribution views',
				})}
			</Toggle>
		</Container>
	</svelte:fragment>
</Modal>

<style>
	.pv-distribution-container {
		width: 100%;
		min-width: 0;
		container-type: inline-size;
	}

	.pv-distribution-container--embedded :global(.controls-container),
	.pv-distribution-container--embedded :global(.controls-container > .container) {
		border: 0 !important;
		border-radius: 0 !important;
		background: transparent !important;
		box-shadow: none !important;
	}

	.pv-distribution-container--embedded :global(.controls-container > .container) {
		padding: 0 !important;
	}

	.pv-distribution-container--embedded :global(.parameter-card-group) {
		border: 0 !important;
		background: transparent !important;
	}

	.pv-distribution-layout {
		display: grid;
		grid-template-columns: minmax(0, 2fr) minmax(220px, 1fr);
		gap: var(--spacing-m);
		width: 100%;
		min-width: 0;
	}

	.pv-distribution-chart,
	.pv-distribution-details {
		min-width: 0;
	}

	.grid {
		min-height: 100%;
		display: grid;
	}

	@container (max-width: 720px) {
		.pv-distribution-layout {
			grid-template-columns: minmax(0, 1fr);
		}

		.pv-distribution-details :global(.live-panel-info) {
			min-width: 0;
		}
	}
</style>
